import Icon, {icons} from './icon'
import FormControl from 'react-bootstrap/lib/FormControl'
import FormGroup from 'react-bootstrap/lib/FormGroup'
import HelpBlock from 'react-bootstrap/lib/HelpBlock'
import PropTypes from 'prop-types'
import React, {PureComponent} from 'react'

import {colors, text} from '../styles'
import {css} from 'glamor'

export const style = {
  base: (compact) => {
    let style = {
      color: colors.inputText,
      background: colors.inputBG,
      borderRadius: '3px',
      boxShadow: 'none',
      border: `1px solid ${colors.inputBorder}`,
      height: '36px',
      outline: 'none',
      padding: '5px 10px',
      textOverflow: 'ellipsis',
      width: '100%'
    }

    if (!compact) {
      style = {
        ...style,
        height: '50px',
        padding: '12px 20px',
        transition: 'padding 0.10s, background 0.10s',
        '&::placeholder': {
          color: colors.inputPlaceholder,
          position: 'absolute',
          top: 0
        },
        '&:focus::placeholder': {
          opacity: 0
        },
        '&:focus, &:not(:placeholder-shown)': {
          background: colors.inputFocus,
          paddingBottom: '6px',
          paddingTop: '18px'
        },
        '&:focus + .input-label': css(text.inputLabel, {
          opacity: 1,
          transform: 'translateY(-3px)'
        }),
        '&:not(:placeholder-shown) + .input-label': css(text.inputLabel, {
          opacity: 1,
          transform: 'translateY(-3px)'
        }),
        '&::-webkit-inner-spin-button': {
          display: 'none'
        }
      }
    }

    return css(style)
  },
  flags: {
    icon: {
      '& .input-control': {
        paddingRight: '40px'
      }
    },
    labelless: {
      '&:focus::placeholder': {
        opacity: 1
      },
      '&:focus, &:not(:placeholder-shown)': {
        padding: '12px 20px'
      }
    }
  },
  hidden: css({
    position: 'absolute'
  }),
  icon: (compact) =>
    css({
      pointerEvents: 'none',
      position: 'absolute',
      top: compact ? '6px' : '13px',
      right: compact ? '10px' : '20px'
    }),
  label: css(text.input, {
    color: colors.inputPlaceholder,
    pointerEvents: 'none',
    position: 'absolute',
    left: '21px',
    opacity: 0,
    top: '13px',
    transition: 'transform 0.10s, font 0.10s'
  }),
  sizes: {
    block: css({
      width: '100%'
    }),
    large: css({
      width: '300px'
    }),
    small: css({
      width: '150px'
    })
  },
  states: {
    invalid: css({
      '& .input-control': {
        borderColor: colors.inputBorderError,
        '& + .input-label': {
          color: colors.inputBorderError
        }
      },
      '& .input-icon': {
        color: colors.inputBorderError
      },
      '& .help-block': {
        color: colors.inputBorderError
      }
    })
  },
  wrapper: (compact) =>
    css(text.input, {
      borderRadius: '3px',
      display: 'inline-block',
      position: 'relative',
      marginBottom: !compact ? '10px' : 'inherit',
      '& + .error-message': {
        marginTop: 0
      }
    })
}

export default class Input extends PureComponent {
  render() {
    const {
      compact,
      className,
      componentClass,
      helpText,
      icon,
      inputRef,
      label,
      name,
      onChange,
      size,
      spinIcon,
      style: customStyle,
      type,
      valid,
      value,
      ...rest
    } = this.props

    let wrapperStyles = style.wrapper(compact)
    let elementStyles = style.base(compact)
    let iconStyles = style.icon(compact)
    let fontAwesomeIcon

    switch (size) {
      case 'block':
      case 'small':
        wrapperStyles = css(wrapperStyles, style.sizes[size])
        break

      case 'large':
      default:
        wrapperStyles = css(wrapperStyles, style.sizes.large)
        break
    }

    if (icon) {
      wrapperStyles = css(wrapperStyles, style.flags.icon)
      fontAwesomeIcon = icons[icon]
    } else if (valid === false) {
      wrapperStyles = css(wrapperStyles, style.flags.icon, style.states.invalid)
      fontAwesomeIcon = icons.times
    }

    if (componentClass === 'textarea') {
      elementStyles = css(elementStyles, css({height: 'auto'}))
    }

    if (!label) {
      elementStyles = css(elementStyles, style.flags.labelless)
    }

    if (type === 'hidden') {
      wrapperStyles = css(wrapperStyles, style.hidden)
    }

    wrapperStyles = css(wrapperStyles, customStyle)

    return (
      <FormGroup className="input-group" {...wrapperStyles}>
        <FormControl
          bsClass={`input-control ${className}`}
          componentClass={componentClass}
          inputRef={inputRef}
          name={name}
          onChange={onChange}
          placeholder={label}
          type={type}
          value={value}
          {...rest}
          {...elementStyles}
        />
        {label && (
          <span className="input-label" {...style.label}>
            {label}
          </span>
        )}
        {fontAwesomeIcon && (
          <div className="input-icon" {...iconStyles}>
            <Icon icon={fontAwesomeIcon} spin={spinIcon} />
          </div>
        )}
        {helpText && <HelpBlock>{helpText}</HelpBlock>}
      </FormGroup>
    )
  }
}

Input.propTypes = {
  compact: PropTypes.bool,
  className: PropTypes.string,
  componentClass: PropTypes.string,
  helpText: PropTypes.string,
  icon: PropTypes.string,
  inputRef: PropTypes.func,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  size: PropTypes.oneOf(['small', 'large', 'block']),
  spinIcon: PropTypes.bool,
  style: PropTypes.object,
  type: PropTypes.string,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

Input.defaultProps = {
  className: '',
  compact: false,
  componentClass: 'input',
  size: 'large',
  spinIcon: false,
  type: 'text'
}
